"use client";

// Notification dropdown — GET /api/notifications, the account's own delivered
// inbox (support tickets, bell escalations, etc.). Read state is server-owned
// (PATCH .../read, .../read-all) — no client-side read tracking anymore.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Icon, { type IconName } from "./Icon";
import { Skeleton } from "./Skeleton";
import { PanelError } from "./PanelState";
import { useToast } from "./Toast";
import { ApiError, notificationsApi, type AppNotification, type PageMeta } from "../lib/api";
import { timeAgo } from "../lib/format";
import { useApi } from "../lib/useApi";
import { bumpNotificationRevision, useNotificationRevision } from "../lib/notificationRevision";

const TYPE_ICON: Record<string, IconName> = {
  SUPPORT: "message",
};

export function notificationIcon(type: string): IconName {
  return TYPE_ICON[type] ?? "bell";
}

/** In-app destination for a notification. Keep links on this dashboard origin,
 *  and deep-link support notifications to their ticket when one is supplied. */
{/*
  ⠀⠀⠀⠀⠀⢸⠓⢄⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠀⢸⠀⠀⠑⢤⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠀⢸⡆⠀⠀⠀⠙⢤⡷⣤⣦⣀⠤⠖⠚⡿⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀
⣠⡿⠢⢄⡀⠀⡇⠀⠀⠀⠀⠀⠉⠀⠀⠀⠀⠀⠸⠷⣶⠂⠀⠀⠀⣀⣀⠀⠀⠀
⢸⣃⠀⠀⠉⠳⣷⠞⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⠉⠉⠉⠉⠉⠉⠉⢉⡭⠋
⠀⠘⣆⠀⠀⠀⠁⠀⢀⡄⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⡴⠋⠀⠀
⠀⠀⠘⣦⠆⠀⠀⢀⡎⢹⡀⠀⠀⠀⠀⠀⠀⠀⠀⡀⠀⠀⡀⣠⠔⠋⠀⠀⠀⠀
⠀⠀⠀⡏⠀⠀⣆⠘⣄⠸⢧⠀⠀⠀⠀⢀⣠⠖⢻⠀⠀⠀⣿⢥⣄⣀⣀⣀⠀⠀
⠀⠀⢸⠁⠀⠀⡏⢣⣌⠙⠚⠀⠀⠠⣖⡛⠀⣠⠏⠀⠀⠀⠇⠀⠀⠀⠀⢙⣣⠄
⠀⠀⢸⡀⠀⠀⠳⡞⠈⢻⠶⠤⣄⣀⣈⣉⣉⣡⡔⠀⠀⢀⠀⠀⣀⡤⠖⠚⠀⠀
⠀⠀⡼⣇⠀⠀⠀⠙⠦⣞⡀⠀⢀⡏⠀⢸⣣⠞⠀⠀⠀⡼⠚⠋⠁⠀⠀⠀⠀⠀
⠀⢰⡇⠙⠀⠀⠀⠀⠀⠀⠉⠙⠚⠒⠚⠉⠀⠀⠀⠀⡼⠁⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⢧⡀⠀⢠⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠙⣞⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠙⣶⣶⣿⠢⣄⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢸⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠀⠉⠀⠀⠀⠙⢿⣳⠞⠳⡄⠀⠀⠀⢀⡞⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠉⠀⠀⠹⣄⣀⡤⠋⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
   */}
export function notificationHref(n: AppNotification): string | null {
  if (n.type === "SUPPORT") {
    const ticketId = n.data?.ticketId;
    return ticketId ? `/support?ticket=${encodeURIComponent(ticketId)}` : "/support";
  }

  const route = n.data?.route?.trim();
  if (route?.startsWith("/") && !route.startsWith("//")) return route;
  return null;
}

export default function NotificationPanel() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  // Dropdown is portaled to <body> (fixed) so it never stretches the header —
  // anchored to the button's viewport rect on open, same as ThemeMenu.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // A push message bumps the revision, which re-fetches the feed so the badge
  // count moves without a page reload.
  const revision = useNotificationRevision();

  const { data, loading, error, reload } = useApi<{ items: AppNotification[]; meta: PageMeta }>(
    (signal) => notificationsApi.list({ limit: 20 }, signal),
    [revision],
    "Failed to load notifications.",
  );

  const items = data?.items ?? [];
  const unread = items.filter((n) => !n.isRead).length;

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // clientWidth excludes the scrollbar, matching rect coordinates.
      setPos({ top: r.bottom + 8, right: document.documentElement.clientWidth - r.right });
      reload();
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    // Fixed-position menu detaches from its anchor on scroll/resize — close it.
    const onMove = () => setOpen(false);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const onMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      // Bumping (rather than just reloading here) also refetches the full
      // Notifications page and any other open subscriber.
      bumpNotificationRevision();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : "Could not mark notifications read",
        tone: "danger",
        icon: "bell",
      });
    }
  };

  const openItem = async (n: AppNotification) => {
    setOpen(false);
    if (!n.isRead) {
      try {
        await notificationsApi.markRead(n.id);
        bumpNotificationRevision();
      } catch {
        // Non-fatal — the notification still opens even if the read receipt fails.
      }
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label="Notifications"
        aria-expanded={open}
        className="neu-btn relative grid place-items-center w-12 h-12 rounded-2xl text-text-muted shrink-0"
      >
        <Icon name="bell" size={19} />
        {unread > 0 && (
          <span
            className="live-dot absolute -top-1 -right-1 min-w-4.5 h-4.5 px-1 grid place-items-center rounded-full text-[10px] font-bold text-white"
            style={{ background: "var(--danger)" }}
          >
            {unread}
          </span>
        )}
      </button>

      {open &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            role="dialog"
            aria-label="Notifications"
            className="glass modal-pop fixed w-[min(92vw,380px)] p-3 rounded-2xl z-60 flex flex-col"
            style={{ top: pos.top, right: pos.right, background: "var(--surface)" }}
          >
            <div className="flex items-center justify-between gap-3 px-1 pb-2.5">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-[15px] text-text">Notifications</h2>
                {unread > 0 && (
                  <span
                    className="chip px-2 py-0.5 rounded-full text-[11px] font-semibold"
                    style={
                      { color: "var(--danger)", "--tint-color": "var(--rose)" } as React.CSSProperties
                    }
                  >
                    {unread}
                  </span>
                )}
              </div>
              {unread > 0 && (
                <button
                  onClick={onMarkAllRead}
                  className="text-[12px] font-medium text-accent hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col gap-2 p-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 rounded-2xl" />
                ))}
              </div>
            ) : error ? (
              <PanelError message={error} onRetry={reload} />
            ) : items.length === 0 ? (
              <p className="text-[12.5px] text-text-faint text-center py-6">Nothing new.</p>
            ) : (
              <ul
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                className="flex flex-col gap-2 max-h-[min(60dvh,420px)] overflow-y-auto -mr-1 pr-1"
              >
                {items.map((n) => {
                  const href = notificationHref(n);
                  const row = (
                    <>
                      <span
                        className="chip grid place-items-center w-9 h-9 rounded-xl shrink-0"
                        style={{ color: "var(--accent)", "--tint-color": "var(--mint)" } as React.CSSProperties}
                      >
                        <Icon name={notificationIcon(n.type)} size={16} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[13px] font-semibold text-text truncate">{n.title}</p>
                          {!n.isRead && (
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: "var(--accent)" }}
                            />
                          )}
                        </div>
                        <p className="text-[12px] text-text-muted truncate">{n.body}</p>
                        <p className="text-[11px] text-text-faint mt-0.5">{timeAgo(n.createdAt)}</p>
                      </div>
                    </>
                  );

                  const cls = `neu-sm flex gap-3 px-3 py-2.5 rounded-2xl text-left transition-colors ${
                    n.isRead ? "opacity-60" : ""
                  } ${href ? "cursor-pointer hover:opacity-100" : ""}`;

                  return (
                    <li key={n.id}>
                      {href ? (
                        <Link href={href} onClick={() => void openItem(n)} className={`${cls} block`}>
                          {row}
                        </Link>
                      ) : (
                        <button onClick={() => void openItem(n)} className={`${cls} w-full`}>
                          {row}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <Link
              href="/notifications"
              onClick={() => setOpen(false)}
              className="neu-btn mt-2.5 h-10 rounded-2xl grid place-items-center text-[12.5px] font-medium text-text-muted hover:text-text transition-colors"
            >
              View all notifications
            </Link>
          </div>,
          document.body,
        )}
    </>
  );
}
